export const enumToList = <T extends object>(e: T) =>
  Object.entries(e).map(([key, value]) => ({ key, value }));

export enum AssetEntity {
  Beacon = 'beacon',
  Device = 'device',
  MqttGateway = 'mqttgateway',
  GpsTracker = 'gpstracker',
  Tool = 'tool',
  Unknown = 'Unknown',
}

export enum OrderDisplayValue {
  Ascending = 'Ascending',
  Descending = 'Descending',
}

export enum BufferShapeType {
  Circle = 'Circle',
  Scaled = 'Scaled',
}

export enum CreateOrUpdate {
  Create = 'CREATE',
  Update = 'UPDATE',
}

export enum DrawType {
  Circle = 'CIRCLE',
  Polygon = 'POLYGON',
  Tripwire = 'TRIPWIRE',
  Multipolygon = 'MULTIPOLYGON',
  MicrofenceBeacon = 'MICROFENCE_BEACON',
  MicrofenceDevice = 'MICROFENCE_DEVICE',
  MicrofenceGateway = 'MICROFENCE_GATEWAY',
  Measure = 'MEASURE',
}

export enum EBTNotifierTypeId {
  Email = 'EMAIL',
  Notification = 'NOTIFICATION',
  Webhook = 'WEBHOOK',
}

export enum EBTNotifierTypeValue {
  Email = 'Email',
  Notification = 'Notification',
  Webhook = 'Webhook',
}

export enum EnteredTypeId {
  Above = 'ABOVE',
  Below = 'BELOW',
  EnterGeofence = 'ENTER_GEOFENCE',
  ExitGeofence = 'EXIT_GEOFENCE',
  DwellGeofence = 'DWELL_GEOFENCE',
  EnteredMicrofence = 'ENTER_MICROFENCE',
  ExitMicrofence = 'EXIT_MICROFENCE',
  Left = 'LEFT',
  Right = 'RIGHT',
}

export enum EBTTriggerTypeId {
  Battery = 'BATTERY',
  Custom = 'CUSTOM',
  EnvironmentalMonitoring = 'ENVIRONMENTALMONITORING',
  Extensometer = 'EXTENSOMETER',
  Inactivity = 'INACTIVITY',
  Location = 'LOCATION',
  ManDown = 'MANDOWN',
  Proximity = 'PROXIMITY',
  Scheduler = 'SCHEDULER',
  Spo2 = 'SPO2',
  Temperature = 'TEMPERATURE',
  Webhook = 'WEBHOOK',
  WelfareCheckResponse = 'WELFARECHECKRESPONSE',
}

export enum EBTTriggerTypeValue {
  Battery = 'Battery',
  Custom = 'Custom',
  EnvironmentalMonitoring = 'Environmental Monitoring',
  Extensometer = 'Extensometer',
  Inactivity = 'Inactivity',
  Location = 'Location',
  ManDown = 'Man Down',
  Proximity = 'Proximity',
  Scheduler = 'Scheduler',
  Spo2 = 'Spo2',
  Temperature = 'Temperature',
  Webhook = 'Webhook',
  WelfareCheckResponse = 'Welfare Check Response',
}

export enum EBTTriggerTypeOptionsId {
  Entry = 'ENTRY',
  Exit = 'EXIT',
  LeftToRight = 'LEFT_TO_RIGHT',
  RightToLeft = 'RIGHT_LEFT',
  Dwell = 'DWELL',
  Below = 'BELOW',
  Above = 'ABOVE',
  NewRule = 'NEW_RULE',
  Get = 'GET',
  Post = 'POST',
  String = 'STRING',
  Number = 'NUMBER',
}

export enum EBTTriggerTypeOptionsValue {
  Entry = 'Entry',
  Exit = 'Exit',
  LeftToRight = 'Left to Right',
  RightToLeft = 'Right to Left',
  Dwell = 'Dwell',
  Below = 'Below',
  Above = 'Above',
  NewRule = 'New rule',
  Get = 'GET',
  Post = 'POST',
  String = 'String',
  Number = 'Number',
}

export enum EBTTriggerSubTypeId {
  ClearedZone = 'CLEARED_ZONE',
  Geofence = 'GEOFENCE',
}

export enum EBTTriggerSubTypeValue {
  ClearedZone = 'Cleared Zone',
  Geofence = 'Geofence',
}

export enum EBTListenerTypeId {
  Geofence = 'geofence',
  Layer = 'layer',
  Microfence = 'microfence',
  Sensor = 'sensor',
}

export enum EBTListenerTypeValue {
  Location = 'Location',
  Proximity = 'Proximity',
  Sensor = 'Sensor (eg. Battery, Temperature, Spo2, etc)',
}

export enum EditType {
  Cut = 'CUT',
  Delete = 'DELETE',
  Move = 'MOVE',
  ScaleRot = 'SCALE_ROT',
  ShapeChange = 'SHAPE_CHANGE',
}

export enum EntityType {
  Asset = 'ASSET',
  Beacon = 'BEACON',
  Device = 'DEVICE',
  Gateway = 'GATEWAY',
  Geofence = 'GEOFENCE',
  Layer = 'LAYER',
  Line = 'LINE',
  Microfence = 'MICROFENCE',
  Multipolygon = 'MULTIPOLYGON',
  Polygon = 'POLYGON',
  Tool = 'TOOL',
  GpsTracker = 'GPS TRACKER',
}

export enum FenceEntityType {
  Line = 'line',
  Microfence = 'microfence',
  Multipolygon = 'multipolygon',
  Polygon = 'polygon',
}

export enum FenceEvent {
  Entered = 'Entered',
  Exited = 'Exited',
  Dwell = 'Dwell',
}

export enum FenceZone {
  none = 'none',
  breach = 'breach',
  buffer = 'buffer',
  cleared = 'cleared',
  loading = 'loading',
  dumping = 'dumping',
  maintenance = 'maintenance',
}

export enum FenceZoneValue {
  Breach = 'Breach',
  Buffer = 'Buffer',
  Cleared = 'Cleared',
  NoZone = 'No Zone',
  Loading = 'Loading',
  Dumping = 'Dumping',
  Maintenance = 'Maintenance',
}

export enum GeofenceOrderByType {
  GeofenceType = 'geofenceType',
  Name = 'name',
}

export enum GeoJsonType {
  Linestring = 'LineString',
  Microfence = 'Microfence',
  Multipolygon = 'MultiPolygon',
  Polygon = 'Polygon',
}

export enum GeofenceEntityType {
  Line = 'line',
  Multipolygon = 'multipolygon',
  Polygon = 'polygon',
}

const GEOFENCE_ENTITY_TYPES: readonly GeofenceEntityType[] = [
  GeofenceEntityType.Line,
  GeofenceEntityType.Multipolygon,
  GeofenceEntityType.Polygon,
];

export const isGeofenceEntityType = (value: unknown): value is GeofenceEntityType =>
  typeof value === 'string' && GEOFENCE_ENTITY_TYPES.some(type => type === value);

export enum GeofenceEntityTypeValue {
  Tripwire = 'Tripwire',
  Multipolygon = 'Multipolygon',
  Polygon = 'Polygon',
}

export enum GeomobyPropertiesValues {
  alertStyle = 'alertStyle',
  alertText = 'alertText',
  alertTimeout = 'alertTimeout',
  dwellSeconds = 'dwellSeconds',
  showAlert = 'showAlert',
}

export enum PortableAssetPropertiesValues {
  inactive = 'inactive',
  visibility = 'visibility',
  emissionsRate = 'emissions_rate',
  emissionsUnit = 'emissions_unit',
  truckingFactor = 'truckingFactor',
  truckingFactorUnit = 'truckingFactor_unit',
  productionCapacity = 'production_capacity',
  productionCapacityUnit = 'production_capacity_unit',
}

export enum Interactions {
  Draw = 'DRAW',
  Modify = 'MODIFY',
}

export enum Method {
  GET = 'GET',
  POST = 'POST',
}

export enum MicrofenceEntity {
  Beacon = 'beacon',
  Device = 'device',
  Gateway = 'gateway',
}

export enum MicrofenceOrderBy {
  Name = 'name',
  Type = 'type',
}

export enum MeasurementType {
  M = 'M',
  M2 = 'M2',
  FT = 'FT',
  FT2 = 'FT2',
  KM = 'KM',
  KM2 = 'KM2',
  MI = 'MI',
  MI2 = 'MI2',
}

export enum MicrofenceZone {
  none = 'none',
  loading = 'loading',
  dumping = 'dumping',
  maintenance = 'maintenance',
}

export enum MicrofenceZoneValue {
  NoZone = 'No Zone',
  Loading = 'Loading',
  Dumping = 'Dumping',
  Maintenance = 'Maintenance',
}

export enum MapType {
  LIVE_MAP = 'LIVE_MAP',
  EDIT_MAP = 'EDIT_MAP',
  REPLAY_MAP = 'REPLAY_MAP',
  HEAT_MAP = 'HEAT_MAP',
}

export enum LocatedAssetType {
  AccessPoint = 'accesspoint',
  EMS = 'ems',
  Gateway = 'gateway',
  Locator = 'locator',
  MDT = 'mdt',
  MqttGateway = 'mqttgateway',
  RFIReader = 'rfiReader',
}

export enum OrderTypeId {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum OrderTypeValue {
  Ascending = 'Ascending',
  Descending = 'Descending',
}

export enum SearchTypeIDs {
  ActiveAssets = 'ACTIVE_ASSETS',
  Beacons = 'BEACONS',
  Devices = 'DEVICES',
  GPSTrackers = 'GPS TRACKERS',
  Geofences = 'GEOFENCES',
  Microfences = 'MICROFENCES',
  Locations = 'LOCATIONS',
  Tools = 'TOOLS',
}

export enum SearchTypeValue {
  ActiveAssets = 'Active Assets',
  Beacons = 'Beacons',
  Devices = 'Devices',
  GPSTrackers = 'GPS Trackers',
  Geofences = 'Geofences',
  Microfences = 'Microfences',
  Locations = 'Locations',
  Tools = 'Tools',
}

export enum PortableAssetClass {
  HV = 'HV',
  LV = 'LV',
  Other = 'Other',
  Personnel = 'Personnel',
}

export enum PortableAssetCategory {
  Bogger = 'bogger',
  Device = 'device',
  Drill = 'drill',
  Integration = 'integration',
  IT = 'IT',
  LightVehicle = 'light vehicle',
  OtherHeavyVehicle = 'other heavy vehicle',
  Personnel = 'personnel',
  Tool = 'tool',
  Truck = 'truck',
  UntrackedAsset = 'untracked asset',
}

export enum AssetModel {
  ArticulatedDumpTruck = 'Articulated_Dump_Truck',
  BigBulldozer = 'Big_Bulldozer',
  CatGrader = 'Cat_Grader',
  CatUndergroundLoader = 'Cat_Underground_Loader',
  DumpTruck = 'Dump_Truck',
  EpirocDiamondDrill = 'EPIROC_Diamond_Drill',
  HeavyExcavator = 'Heavy_Excavator',
  KubotaRtv = 'Kubota_RTV',
  LandfillCompactor = 'Landfill_Compactor',
  LightVehicle = 'Light_Vehicle',
  LittleDumpTruck = 'Little_Dump_Truck',
  MackWaterTruck = 'Mack_Water_Truck',
  MinersHelmet = 'Miners_Helmet',
  MiningExcavator = 'Mining_Excavator',
  MiningTruck = 'Mining_Truck',
  NormetAgitator = 'Normet_Agitator',
  NormetChargeRig = 'Normet_Charge_Rig',
  NormetShotcreteSprayer = 'Normet_Shotcrete_Sprayer',
  NormetSpraymec = 'Normet_Spraymec',
  NormetTransmixer = 'Normet_Transmixer',
  RefugeChamber = 'Refuge_Chamber',
  SandvikDiamondDrill = 'Sandvik_Diamond_Drill',
  SandvikJumboDrill = 'Sandvik_Jumbo_Drill',
  SandvikLoader = 'Sandvik_Loader',
  SandvikProductionDrill = 'Sandvik_Production_Drill',
  SandvikTruck = 'Sandvik_Truck',
  Telehandler = 'Telehandler',
  UndergroundLoader = 'Underground_Loader',
  VolvoItc = 'Volvo_ITC',
  WheelLoaderSmall = 'Wheel_Loader_Small',
}

// Mirrors pybar-web: a model counts as a vehicle when it is set and is neither
// personnel (Miner's Helmet) nor a non-vehicle asset (Refuge Chamber).
export const isVehicleModel = (model?: AssetModel | string): boolean =>
  !!model && model !== AssetModel.MinersHelmet && model !== AssetModel.RefugeChamber;
